import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonLabel", "modal", "modalTitle", "requestList"]

  static values = {
    archiveUrl: String,
    frameId: { type: String, default: "maintenance_requests_table" },
  }

  connect() {
    this.updateButton()
    this.element.addEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.addEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.removeEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  get boundOnSelectionUpdated() {
    if (!this._boundOnSelectionUpdated) {
      this._boundOnSelectionUpdated = () => this.updateButton()
    }
    return this._boundOnSelectionUpdated
  }

  get boundOnFrameRender() {
    if (!this._boundOnFrameRender) {
      this._boundOnFrameRender = (event) => {
        if (event.target?.id === this.frameIdValue) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnFrameRender
  }

  get boundOnSubmitEnd() {
    if (!this._boundOnSubmitEnd) {
      this._boundOnSubmitEnd = (event) => {
        if (event.detail?.success) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnSubmitEnd
  }

  updateButton() {
    if (!this.hasButtonTarget || !this.hasButtonLabelTarget) return

    const { ids, names, count } = this.getCompletedToArchive()
    if (count === 0) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("btn-disabled")
      this.buttonLabelTarget.textContent = "Archive Completed"
    } else {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("btn-disabled")
      this.buttonLabelTarget.textContent = `Archive Completed (${count})`
    }

    this._pendingIds = ids
    this._pendingNames = names
  }

  getCompletedToArchive() {
    const rows = document.querySelectorAll('[data-print-selection-target="row"]')
    const checkboxes = document.querySelectorAll('[data-print-selection-target="rowCheckbox"]')
    const checkedIds = new Set(
      Array.from(checkboxes)
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
    )

    const completed = []

    rows.forEach((row) => {
      const status = row.dataset.status
      const checkbox = row.querySelector('[data-print-selection-target="rowCheckbox"]')
      const id = checkbox?.value
      if (status === "Work Completed" && id) {
        const isRelevant = checkedIds.size === 0 ? true : checkedIds.has(id)
        if (isRelevant) {
          completed.push({
            id,
            name: row.dataset.maintenanceRequestName || id,
          })
        }
      }
    })

    return {
      ids: completed.map((item) => item.id),
      names: completed.map((item) => item.name),
      count: completed.length,
    }
  }

  openModal() {
    const { ids, names, count } = this.getCompletedToArchive()
    if (count === 0) return

    this._pendingIds = ids
    this._pendingNames = names

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = `Archive Completed Maintenance Requests (${count})`
    }
    if (this.hasRequestListTarget) {
      this.requestListTarget.innerHTML = ""
      names.forEach((name) => {
        const badge = document.createElement("span")
        badge.className = "badge badge-outline"
        badge.textContent = name
        this.requestListTarget.appendChild(badge)
      })
    }

    this.modalTarget.showModal()
  }

  closeModal() {
    this.modalTarget.close()
  }

  archive() {
    const ids = this._pendingIds
    if (!ids || ids.length === 0) return

    fetch(this.archiveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
        Accept: "application/json",
      },
      body: JSON.stringify({ ids }),
    })
      .then((response) => {
        if (response.ok) {
          this.closeModal()
          window.toast?.("Archived successfully.", "success")
          window.Turbo?.visit(window.location.href, { action: "replace" })
        } else {
          window.toast?.("Failed to archive maintenance requests.", "error")
        }
      })
      .catch(() => {
        window.toast?.("Failed to archive maintenance requests.", "error")
      })
  }
}
